import React from "react";
import { Bar } from "react-chartjs-2";
import "chart.js/auto";
import { ChartsLegend } from "./ChartsLegend";
import ChartDataLabels from "chartjs-plugin-datalabels";
import {
  AnwesenheitAuslastung,
  BuchungsAuslastung,
  StandortAuslastungItem,
} from "../../models/admin-dashboard-entities";

const DashboardBarChart: React.FC<{
  auslastungData?: Record<string, BuchungsAuslastung>;
  anwesenheitData?: Record<string, AnwesenheitAuslastung>;
  standortData?: Record<string, StandortAuslastungItem>;
  color?: string;
  coloredValueTitle: string;
  grayValueTitle: string;
}> = ({
  auslastungData,
  anwesenheitData,
  standortData,
  coloredValueTitle,
  grayValueTitle,
  color,
}) => {
  let inputData: Record<string, any>;
  if (auslastungData) {
    inputData = auslastungData;
  }
  if (anwesenheitData) {
    inputData = anwesenheitData;
  }
  if (standortData) {
    inputData = standortData;
  }

  const generateChartData = () => {
    const LABEL_MAX_LENGTH = 20;

    const pctRem: number[] = [];
    const pctUsed: number[] = [];
    const originalUsed: number[] = [];
    const originalCap: number[] = [];
    const percentageLabels: string[] = [];

    const parseData = (used: number, cap: number, percentageValue: number) => {
      const p = cap > 0 ? (used / cap) * 100 : 0;
      pctUsed.push(Math.min(100, Math.max(0, p)));
      pctRem.push(p < 100 ? 100 - p : 0);
      originalUsed.push(used);
      originalCap.push(cap);
      percentageLabels.push(`${percentageValue}%`);
    };

    if (auslastungData) {
      Object.values(auslastungData).forEach((data: BuchungsAuslastung) => {
        parseData(data.aktiveAnmeldungen, data.kapa, data.auslastung);
      });
    }
    if (anwesenheitData) {
      Object.values(anwesenheitData).forEach((data: AnwesenheitAuslastung) => {
        parseData(data.checkIns, data.teilnehmer, data.auslastung);
      });
    }
    if (standortData) {
      Object.values(standortData).forEach((data: StandortAuslastungItem) => {
        parseData(data.abrechenbareMitglieder, data.kapa, data.auslastung);
      });
    }

    let displayLabels: string[];

    if (auslastungData || anwesenheitData) {
      displayLabels = Object.keys(inputData).map((label: string) => {
        return label.split("|")[0].trim();
      });
    } else {
      displayLabels = Object.keys(inputData).map((label: string) => {
        return label.length > LABEL_MAX_LENGTH
          ? `${label.substring(0, LABEL_MAX_LENGTH)}...`
          : label;
      });
    }

    return {
      labels: displayLabels,
      datasets: [
        {
          label: coloredValueTitle,
          data: pctUsed,
          backgroundColor: color ? color : "#44AEB0",
          borderColor: color ? color : "#44AEB0",
          barThickness: 12,
        },
        {
          label: grayValueTitle,
          data: pctRem,
          backgroundColor: "#C1C9CD",
          borderColor: "#C1C9CD",
          barThickness: 12,
        },
      ],
      percentageLabels,
      displayedLongLabels: Object.keys(inputData),
      originalUsed,
      originalCap,
    };
  };

  const chartData = generateChartData();

  return (
    <div
      style={{
        height: 20 * Object.keys(inputData).length + 150,
        width: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div style={{ flex: 1, minHeight: 0 }}>
        <Bar
          data={chartData}
          options={{
            interaction: {
              mode: "index",
            },
            indexAxis: "y",
            maintainAspectRatio: false,
            elements: {
              bar: {
                borderWidth: 2,
              },
            },
            scales: {
              x: {
                min: 0,
                max: 125,
                display: false,
                stacked: true,
                grid: { display: false },
              },
              y: {
                stacked: true,
                grid: { display: false },
              },
            },
            plugins: {
              legend: {
                display: false,
              },
              tooltip: {
                callbacks: {
                  title: function (context: any) {
                    const displayLabel =
                      chartData.displayedLongLabels[context[0].dataIndex];
                    return displayLabel;
                  },
                  label: function (context: any) {
                    const datasetLabel = context.dataset.label || "";
                    const dataIndex = context.dataIndex;
                    const originalValue =
                      context.datasetIndex === 0
                        ? chartData.originalUsed[dataIndex]
                        : chartData.originalCap[dataIndex];
                    return `${datasetLabel}: ${originalValue}`;
                  },
                },
              },
              datalabels: {
                color: "#444",
                font: {
                  size: 12,
                },
                padding: 4,
                anchor: "end",
                align: function (context) {
                  const pctUsed =
                    context.chart.data.datasets[0].data[context.dataIndex];
                  const pctRem =
                    context.chart.data.datasets[1].data[context.dataIndex];
                  return pctRem < pctUsed ? "right" : "end";
                },
                formatter: function (_, context) {
                  if (context.datasetIndex === 1) {
                    return chartData.percentageLabels[context.dataIndex];
                  } else {
                    return null;
                  }
                },
              },
            },
          }}
          plugins={[ChartDataLabels]}
        />
      </div>
      <div style={{ paddingTop: 8 }}>
        <ChartsLegend
          usedValueTitle={coloredValueTitle}
          capValueTitle={grayValueTitle}
          usedValueBgColor={color ? color : "#44AEB0"}
        />
      </div>
    </div>
  );
};

export default DashboardBarChart;
